
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface GlobalPricing {
  price_30min: number;
  price_60min: number;
  currency: string;
}

export const useGlobalPricing = (psychologistId?: string) => {
  const [pricing, setPricing] = useState<GlobalPricing>({
    price_30min: 150.00,
    price_60min: 280.00,
    currency: 'BRL'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadPricing = async () => {
    if (!psychologistId) return;
    
    setIsLoading(true);
    try {
      console.log('🔍 [useGlobalPricing] Carregando preços para:', psychologistId);
      
      const { data, error } = await supabase
        .from('psychologist_pricing')
        .select('price_30min, price_60min, currency')
        .eq('psychologist_id', psychologistId)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ [useGlobalPricing] Erro ao carregar preços:', error);
        throw error;
      }
      
      if (data) {
        console.log('✅ [useGlobalPricing] Preços carregados:', data);
        setPricing({
          price_30min: data.price_30min || 150.00,
          price_60min: data.price_60min || 280.00,
          currency: data.currency || 'BRL'
        });
      }
    } catch (error) {
      console.error('🔥 [useGlobalPricing] Erro ao carregar preços:', error);
      toast.error('Erro ao carregar preços');
    } finally {
      setIsLoading(false);
    }
  };

  const savePricing = async (newPricing: Omit<GlobalPricing, 'currency'>) => {
    if (!psychologistId) {
      toast.error('ID do psicólogo não encontrado');
      return false;
    }

    setIsSaving(true);
    try {
      console.log('💰 [useGlobalPricing] Salvando preços:', { psychologistId, newPricing });

      const { error } = await supabase.rpc('save_global_pricing', {
        p_psychologist_id: psychologistId,
        p_price_30min: newPricing.price_30min,
        p_price_60min: newPricing.price_60min
      });

      if (error) {
        console.error('❌ [useGlobalPricing] Erro ao salvar preços:', error);
        throw error;
      }
      
      console.log('✅ [useGlobalPricing] Preços salvos com sucesso');
      setPricing(prev => ({ ...prev, ...newPricing }));
      toast.success('Preços salvos com sucesso!');
      return true;
    } catch (error: any) {
      console.error('🔥 [useGlobalPricing] Erro ao salvar preços:', error);
      toast.error(`Erro ao salvar preços: ${error.message}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (psychologistId) {
      loadPricing();
    }
  }, [psychologistId]);

  return {
    pricing,
    isLoading,
    isSaving,
    loadPricing,
    savePricing
  };
};
